/* ==========================================================================
   Sticky Add to Cart (default product template)

   A presentational bar that mirrors the main product state and forwards its
   click to the real add-to-cart button. No second form, no duplicated variant
   state — the existing ProductVariants / ProductForm pipeline stays the single
   source of truth.

   On products with more than one variant, the "Button on products with
   options" section setting can put the bar into "select" mode instead of
   "add to cart" mode: the button then always reads "Select variant(s)" and,
   on click, either scrolls to the variant picker (anchor mode) or opens the
   theme's existing Quick view popup (modal mode) rather than forwarding to
   the real add-to-cart button.

   Hooks it relies on (all already present in the default section):
     - #add-to-cart-{id}                      product-form wrapper
     - #add-to-cart-{id} [data-js-product-add-to-cart]       real submit button
     - #product-form-{id} [data-js-product-add-to-cart-text] real button label
     - #product-price-{id} [data-js-product-price-original]  live price
     - #product-price-{id} [data-js-product-price-compare]   live compare price
     - product-variants[data-id="{id}"]       variant controller (.currentVariant)
     - document 'VARIANT_CHANGE' event         fires on every variant change
     - .mount-header.is-sticky                 sticky nav state (for top glue)
     - .sticky-atc__quick-view-trigger button   forwards to <quick-view-product>
       (modal mode only; theme's component-quick-buy.js defines that element)
   ========================================================================== */

if (typeof customElements.get('sticky-add-to-cart') === 'undefined') {

  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this._onVariantChange = this._syncState.bind(this);
      this._onButtonClick = this._handleClick.bind(this);
      this._onScroll = this._update.bind(this);
      this._rafPending = false;
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.selectMode = this.dataset.selectMode || 'off';
      this.optionCount = parseInt(this.dataset.optionCount || '0', 10);
      this.labelSelectVariant = this.dataset.labelSelectVariant || '';
      this.labelSelectVariants = this.dataset.labelSelectVariants || '';

      // Resolve the elements we mirror / forward to.
      this.productForm = document.getElementById(`add-to-cart-${this.sectionId}`);
      this.realButton = this.productForm
        ? this.productForm.querySelector('[data-js-product-add-to-cart]')
        : null;
      this.realButtonText = document.querySelector(
        `#product-form-${this.sectionId} [data-js-product-add-to-cart-text]`
      );
      this.productVariants = document.querySelector(
        `product-variants[data-id="${this.sectionId}"]`
      );
      // Prefer the nodes ProductVariants itself updates: the page carries a
      // cloned mobile header, so a selector can land on a stale duplicate.
      this.priceOriginalSource = (this.productVariants && this.productVariants.priceOriginal) || document.querySelector(
        `#product-price-${this.sectionId} [data-js-product-price-original]`
      );
      this.priceCompareSource = (this.productVariants && this.productVariants.priceCompare) || document.querySelector(
        `#product-price-${this.sectionId} [data-js-product-price-compare]`
      );

      // Local nodes.
      this.button = this.querySelector('[data-js-sticky-atc-button]');
      this.buttonText = this.querySelector('[data-js-sticky-atc-button-text]');
      this.summaryEl = this.querySelector('[data-js-sticky-atc-summary]');
      this.priceOriginalEl = this.querySelector('[data-js-sticky-atc-price-original]');
      this.priceCompareEl = this.querySelector('[data-js-sticky-atc-price-compare]');
      this.mediaImg = this.querySelector('.sticky-atc__img');
      this.mountHeader = document.querySelector('.mount-header');
      this.quickViewTrigger = this.querySelector('.sticky-atc__quick-view-trigger button');

      // Without a real button there is nothing to forward to — stay hidden.
      if (!this.realButton) return;

      this.button.addEventListener('click', this._onButtonClick);
      document.addEventListener('VARIANT_CHANGE', this._onVariantChange);

      // Hide whenever the main form is hidden (coming soon).
      this._initFormObserver();

      // A single rAF-throttled handler drives BOTH the reveal decision and the
      // top offset. Scroll position (not IntersectionObserver) is used for the
      // reveal so fast/jump scrolls can't skip the trigger.
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
      this._update();

      // Prime state from the current variant.
      this._syncState();
    }

    disconnectedCallback() {
      document.removeEventListener('VARIANT_CHANGE', this._onVariantChange);
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
      if (this.button) this.button.removeEventListener('click', this._onButtonClick);
      if (this._formObserver) this._formObserver.disconnect();
      if (this._htmlObserver) this._htmlObserver.disconnect();
      if (this._bodyObserver) this._bodyObserver.disconnect();
    }

    /* --- Visibility ------------------------------------------------------ */

    _initFormObserver() {
      // Mirror coming-soon / hidden-form: if the product form is hidden, the bar
      // must never appear. Watch for style/class changes that hide it.
      this._formObserver = new MutationObserver(() => {
        if (this._isFormHidden()) this._hide(true);
      });
      this._formObserver.observe(this.productForm, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });

      // Drawers (cart/search/menu) toggle `kill-overflow` on <html> without
      // firing scroll, so re-evaluate visibility when that class changes.
      this._htmlObserver = new MutationObserver(() => this._update());
      this._htmlObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });

      // basicLightbox (the quick-view popup, size-chart modal, etc.) appends
      // and removes its wrapper directly on <body> without touching
      // `kill-overflow`, so watch body childList too.
      this._bodyObserver = new MutationObserver(() => {
        this._hideSelfLinkInLightbox();
        this._update();
      });
      this._bodyObserver.observe(document.body, { childList: true });
    }

    // The quick-view popup always offers a "visit product page" link — useless
    // when the popup was opened from the very page it links to, so hide it.
    // Handles are compared (not full pathnames) because the page may live
    // under a /collections/... URL while product.url does not.
    _hideSelfLinkInLightbox() {
      const container = document.querySelector('.basicLightbox .product-quick-view-container');
      if (!container) return;
      const handleOf = (path) => {
        const m = /\/products\/([^/?#]+)/.exec(path);
        return m ? m[1] : null;
      };
      const currentHandle = handleOf(window.location.pathname);
      if (!currentHandle) return;
      container.querySelectorAll('a.button--outline').forEach((link) => {
        if (handleOf(link.pathname) === currentHandle) {
          link.classList.add('sticky-atc-hidden');
        }
      });
    }

    _isFormHidden() {
      if (!this.productForm) return true;
      const style = window.getComputedStyle(this.productForm);
      return style.display === 'none' || style.visibility === 'hidden';
    }

    _isOverlayOpen() {
      return document.documentElement.classList.contains('kill-overflow')
        || !!document.querySelector('.basicLightbox');
    }

    _show() {
      if (this._isFormHidden()) return;
      if (this._visible) return;
      this._visible = true;
      this.hidden = false;
      // Force a frame so the transition runs from the hidden transform.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (this._visible) this.classList.add('is-visible');
        });
      });
    }

    _hide(immediate) {
      if (!this._visible && this.hidden) return;
      this._visible = false;
      this.classList.remove('is-visible');
      if (immediate) {
        this.hidden = true;
        return;
      }
      // Remove from the layout/tab order after the transition completes.
      const onEnd = (e) => {
        if (e && e.target !== this) return;
        if (!this._visible) this.hidden = true;
        this.removeEventListener('transitionend', onEnd);
      };
      this.addEventListener('transitionend', onEnd);
      // Fallback in case transitionend doesn't fire.
      window.clearTimeout(this._hideTimer);
      this._hideTimer = window.setTimeout(() => {
        if (!this._visible) this.hidden = true;
      }, 400);
    }

    /* --- Reveal + top offset (one rAF-throttled pass) -------------------- */

    _update() {
      if (this._rafPending) return;
      this._rafPending = true;
      window.requestAnimationFrame(() => {
        this._rafPending = false;

        // Glue under the nav when it's sticky (top mode consumes this var).
        let top = 0;
        if (this.mountHeader && this.mountHeader.classList.contains('is-sticky')) {
          top = Math.max(0, this.mountHeader.getBoundingClientRect().bottom);
        }
        this.style.setProperty('--sticky-atc-top', `${top}px`);

        // Hide while any drawer/modal is open (cart, search, menu, quick view) —
        // the theme locks scroll with `kill-overflow` on <html>, and the
        // quick-view / size-chart popups append a `.basicLightbox` node to
        // <body>. Avoids the bar punching through an overlay and is the
        // expected native behaviour.
        if (this._isFormHidden() || this._isOverlayOpen()) {
          this._hide(true);
          return;
        }

        // Reveal once the real add-to-cart button has scrolled above the top
        // edge of the viewport; hide again when it returns. Computed from the
        // live rect so jump/fling scrolls can't skip the trigger.
        const btnRect = this.realButton.getBoundingClientRect();
        if (btnRect.bottom <= 0) {
          this._show();
        } else {
          this._hide();
        }
      });
    }

    /* --- State mirror ---------------------------------------------------- */

    _syncState() {
      const variant = this.productVariants ? this.productVariants.currentVariant : null;

      if (this.selectMode === 'anchor') {
        // Anchor select mode: the label always prompts for a selection and the
        // button stays enabled even if the current variant is sold out.
        if (this.buttonText) {
          const label = this.optionCount > 1 ? this.labelSelectVariants : this.labelSelectVariant;
          if (label) this.buttonText.textContent = label;
        }
        this.button.classList.remove('is-unavailable');
        this.button.setAttribute('aria-disabled', 'false');
      } else {
        // Button label — mirror the real button's text verbatim (covers add to
        // cart / sold out / pre-order / select options in the theme's locale).
        if (this.buttonText && this.realButtonText) {
          const txt = this.realButtonText.textContent.trim();
          if (txt) this.buttonText.textContent = txt;
        }

        if (this.selectMode === 'off') {
          // Availability visual state mirrors the real form.
          const soldOut = this.productForm.classList.contains('disabled-cart')
            || (this.realButton && this.realButton.classList.contains('disabled'));
          const unavailable = this.productForm.classList.contains('unavailable-variant');
          this.button.classList.toggle('is-unavailable', soldOut);
          this.button.setAttribute('aria-disabled', (soldOut || unavailable) ? 'true' : 'false');
        } else {
          // Modal mode keeps the add-to-cart label but opens the quick-view
          // popup on click, so it stays enabled even for a sold-out variant.
          this.button.classList.remove('is-unavailable');
          this.button.setAttribute('aria-disabled', 'false');
        }
      }

      // Label = the selected variant options (e.g. "short - black - two pack
      // with two trays"); "none (planter only)" collapses to "planter only".
      // Single-variant products keep their server-rendered title.
      if (this.summaryEl && variant && Array.isArray(variant.options)) {
        const isDefault = variant.options.length === 0
          || (variant.options.length === 1 && /^default title$/i.test(variant.options[0]));
        if (!isDefault) {
          const summary = variant.options
            .map((opt) => {
              const m = /none\s*\((.+)\)/i.exec(opt);
              return (m ? m[1] : opt).trim();
            })
            .join(' - ')
            .toLowerCase();
          if (summary) this.summaryEl.textContent = summary;
        }
      }

      // Price — copy from the live price block (reuses theme money formatting).
      if (this.priceOriginalEl && this.priceOriginalSource) {
        this.priceOriginalEl.innerHTML = this.priceOriginalSource.innerHTML;
      }
      if (this.priceCompareEl && this.priceCompareSource) {
        this.priceCompareEl.innerHTML = this.priceCompareSource.innerHTML;
      }

      // Thumbnail — swap to the variant image when present.
      if (this.mediaImg && variant && variant.featured_media && variant.featured_media.preview_image) {
        const src = variant.featured_media.preview_image.src;
        if (src) {
          const w = (s, width) => {
            // Shopify CDN URLs accept a width query param.
            try {
              const u = new URL(s, window.location.origin);
              u.searchParams.set('width', width);
              return u.toString();
            } catch (e) {
              return s;
            }
          };
          this.mediaImg.src = w(src, 144);
          this.mediaImg.srcset = `${w(src, 144)} 1x, ${w(src, 288)} 2x`;
        }
      }

      // If the form became hidden (coming soon), make sure the bar is gone.
      if (this._isFormHidden()) this._hide(true);
    }

    /* --- Forward the click ------------------------------------------------ */

    _handleClick(e) {
      e.preventDefault();

      // Anchor mode always guides the customer to the variant picker.
      if (this.selectMode === 'anchor') {
        this._scrollToPicker();
        return;
      }

      // Modal mode opens the theme's existing Quick view popup so the
      // customer can finish choosing options there; falls back to the
      // in-page picker if the quick-view component isn't available.
      if (this.selectMode === 'modal') {
        if (this.quickViewTrigger && typeof customElements.get('quick-view-product') !== 'undefined') {
          this.quickViewTrigger.click();
          this._hide(true);
        } else {
          this._scrollToPicker();
        }
        return;
      }

      // "off" mode — forward straight to the real add-to-cart button.

      // Required option not chosen → guide the user to the picker instead.
      if (this.productForm.classList.contains('unavailable-variant')) {
        this._scrollToPicker();
        return;
      }

      // Sold out / disabled → no-op (the label already shows "Sold out").
      const soldOut = this.productForm.classList.contains('disabled-cart')
        || (this.realButton && this.realButton.classList.contains('disabled'));
      if (soldOut) return;

      // Loading affordance; cleared when the form reports the add.
      this.button.classList.add('is-loading');
      const clear = () => {
        this.button.classList.remove('is-loading');
        this.productForm.removeEventListener('add-to-cart', clear);
      };
      this.productForm.addEventListener('add-to-cart', clear);
      window.clearTimeout(this._loadTimer);
      this._loadTimer = window.setTimeout(clear, 4000);

      // Reuse the entire real add-to-cart pipeline.
      this.realButton.click();
    }

    _scrollToPicker() {
      const picker = document.querySelector(
        `#product-${this.sectionId} product-variants[data-id="${this.sectionId}"], #product-${this.sectionId} .product-variants`
      );
      if (!picker) return;
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      picker.classList.add('sticky-atc-pulse');
      window.setTimeout(() => picker.classList.remove('sticky-atc-pulse'), 1200);
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
